// Cloud Vocabulary tab.
//
// Two-panel layout: left is the scheme/concept tree, right is the
// detail panel for the currently selected concept. Phases 1-4 in tree:
// Phase 1 tree, Phase 2 search + concept detail, Phase 3 create
// scheme + concept, Phase 4 edit (prefLabel + definition) + delete
// (leaf-only, server-enforced).
//
// Design rules from CLAUDE.local.md and memory:
//   - Taxonomist-first: URIs are visible but secondary to prefLabels.
//   - Alphabetical ordering throughout (already enforced server-side).
//   - Schemes render at the top with a description; concepts nest below.
//   - Edit affordances are inline (no modal) so a taxonomist can fix
//     a typo without leaving context. Delete shows an inline confirm
//     strip; a 409 from the server (concept has narrower terms) is
//     surfaced verbatim so the user knows which children to clear first.
//
// Vocabulary is a usage/role partition: the tree shows everything in the
// vocabulary LAYER regardless of which named graph holds it. There is no graph
// selector (the backend layer-scopes the tree and ignores the graph); the fixed
// VOCABULARY_GRAPH is only the write target for new terms and classes.

import { useEffect, useMemo, useState } from "react";
import { invalidateAfterEdit } from '@/lib/invalidate';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchVdbTopClasses } from "@/lib/useVdb";
import { useAuth } from "react-oidc-context";
import { ALL_GRAPHS } from "@/components/GraphSelector";
import {
  MoveConceptModal,
  NewConceptModal,
  NewSchemeModal,
  type MovePreviousParent,
  type ParentContext,
  EditSchemeModal,
  DeleteSchemeModal,
  type SchemeDeleteChoice,
} from "@/components/VocabCreateModals";
import { canImportData, isQros, useMe } from "@/lib/useMe";
import { EditModeButton } from '@/lib/editMode';
import { useEntity } from "@/lib/useEntity";
import { DocExportButtons } from "@/components/DocExportButtons";
import { CopyUriButton, EntityView } from "@/pages/EntityInfoPage";
import { VdbBuildingNotice } from "@/components/VdbBuildingNotice";
import { cn } from "@/lib/utils";
import {
  collectAllUris,
  deleteConcept,
  deleteScheme,
  filterTree,
  patchConcept,
  useVocabConcept,
  useVocabTree,
  type AnnotationValue,
  type ConceptDetail,
  type ConceptNode,
  type LangValue,
  type PatchConceptRequest,
  type SchemeNode,
  type UriLabel,
  type VocabTreeResponse,
} from "@/lib/useVocab";
import { AnnotationListEditor } from "@/components/AnnotationListEditor";
import { CONCEPT_ANNOTATION_PREDICATES } from "@/lib/useOntology";
import { Message, messageClass } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';

/** The concept's annotations of the given predicates, as displayable
 *  lang-values (order preserved from the detail read). */
function annotationValues(
  detail: ConceptDetail,
  ...predicates: string[]
): LangValue[] {
  return detail.annotations
    .filter((a) => predicates.includes(a.predicate))
    .map((a) => (a.lang ? { value: a.value, lang: a.lang } : { value: a.value }));
}

const VOCABULARY_GRAPH = "urn:tres:vocabulary";

/** How long the post-Move undo banner stays visible. Long enough for
 *  a second-thought, short enough not to clutter the page. */
const MOVE_UNDO_WINDOW_MS = 60_000;

export function VocabularyPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const me = useMe();
  // Vocabulary is a usage/role partition: the tree shows everything in the
  // vocabulary LAYER regardless of which named graph holds it, so there is no
  // graph selector (parallels the catalog hard-scope). The backend ignores this
  // value for the layer-scoped tree; it is the write target for new terms and
  // classes, which land in the vocabulary graph.
  const graph = VOCABULARY_GRAPH;
  const [selectedUri, setSelectedUri] = useState<string | null>(null);
  // A vocabulary CLASS (scheme) selection — even one with zero instances must
  // be inspectable (its URI verifies upload mappings). Mutually exclusive with
  // the concept selection.
  const [selectedClassUri, setSelectedClassUri] = useState<string | null>(null);
  const classEntity = useEntity(selectedClassUri, ALL_GRAPHS);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [schemeModalOpen, setSchemeModalOpen] = useState(false);
  const [schemeToEdit, setSchemeToEdit] = useState<SchemeNode | null>(null);
  const [schemeToDelete, setSchemeToDelete] = useState<SchemeNode | null>(null);
  // A delete runs behind a CLOSED modal (UAT 2026-09-21: "modal stays on screen
  // during working"): the scheme leaves the tree the moment the user confirms,
  // the panel says what is being deleted while it runs, and a refusal prints
  // there too — with the tree refetched so the scheme comes back.
  const [deleting, setDeleting] = useState<{ label: string; members: number } | null>(null);
  const [deleteProblem, setDeleteProblem] = useState<string | null>(null);
  const dropSchemeFromTree = (uri: string) => {
    queryClient.setQueryData<VocabTreeResponse>(
      ["vocab", "tree", graph ?? "", token ? "auth" : "noauth"],
      (cur) => (cur ? { ...cur, schemes: cur.schemes.filter((s) => s.uri !== uri) } : cur),
    );
  };
  const runSchemeDelete = async (target: SchemeNode, choice: SchemeDeleteChoice) => {
    if (!token || !graph) return;
    setDeleteProblem(null);
    setDeleting({ label: target.pref_label, members: choice.members });
    if (selectedUri === target.uri) setSelectedUri(null);
    dropSchemeFromTree(target.uri);
    const result = await deleteScheme(
      { uri: target.uri, graph, ...(choice.reassignTo ? { reassignTo: choice.reassignTo } : {}) },
      token,
    );
    setDeleting(null);
    if (!result.ok) setDeleteProblem(`${target.pref_label} was not deleted: ${result.message}`);
    invalidateAfterEdit(queryClient, null);
  };
  const [conceptModalParent, setConceptModalParent] =
    useState<ParentContext | null>(null);
  // When non-null, the Move-concept modal is open targeting this concept.
  const [moveModalFor, setMoveModalFor] = useState<{
    uri: string;
    label: string;
  } | null>(null);
  // Recent-move state for the Undo banner. Cleared automatically
  // after MOVE_UNDO_WINDOW_MS or by manual dismiss / a follow-up
  // move / graph change.
  const [recentMove, setRecentMove] = useState<{
    conceptUri: string;
    conceptLabel: string;
    previousParent: MovePreviousParent;
  } | null>(null);
  const [undoBusy, setUndoBusy] = useState(false);

  const token = auth.user?.id_token;
  const [editMode, setEditMode] = useState(false);
  const canEdit = canImportData(me.data) && editMode;
  // QROS-native definition tags — the RDF version surfaces none.
  const qros = isQros(me.data);
  const treeQuery = useVocabTree(graph);
  const detailQuery = useVocabConcept(selectedUri, graph);

  // Apply the search filter to the loaded tree. Pure function; cheap.
  const filteredTree = useMemo(() => {
    if (!treeQuery.data) return null;
    return filterTree(treeQuery.data, searchQuery);
  }, [treeQuery.data, searchQuery]);

  // When a search is active, auto-expand every node still visible so
  // matches and their ancestor chains are revealed without the user
  // hand-clicking through.
  useEffect(() => {
    if (!filteredTree || searchQuery.trim() === "") return;
    setExpanded(new Set(collectAllUris(filteredTree)));
  }, [filteredTree, searchQuery]);

  // Auto-expand all schemes + top-level concepts on first successful
  // load so the user sees the structure without clicking. Guarded by
  // a did-init flag (not `expanded.size === 0`) so Collapse all isn't
  // immediately undone by this effect.
  const [didAutoExpand, setDidAutoExpand] = useState(false);
  useEffect(() => {
    if (!treeQuery.data || didAutoExpand) return;
    const initial = new Set<string>();
    for (const scheme of treeQuery.data.schemes) {
      initial.add(scheme.uri);
      for (const top of scheme.top_concepts) {
        initial.add(top.uri);
      }
    }
    setExpanded(initial);
    setDidAutoExpand(true);
  }, [treeQuery.data, didAutoExpand]);

  function toggleNode(uri: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uri)) next.delete(uri);
      else next.add(uri);
      return next;
    });
  }

  const totalCounts = useMemo(
    () => countNodes(treeQuery.data),
    [treeQuery.data],
  );
  const filteredCounts = useMemo(
    () => countNodes(filteredTree ?? undefined),
    [filteredTree],
  );
  const isFiltering = searchQuery.trim() !== "";

  // Clear the undo banner on graph change or when the timer fires.
  useEffect(() => {
    setRecentMove(null);
  }, [graph]);

  useEffect(() => {
    if (!recentMove) return;
    const t = setTimeout(() => setRecentMove(null), MOVE_UNDO_WINDOW_MS);
    return () => clearTimeout(t);
  }, [recentMove]);

  async function handleUndoMove() {
    if (!token || !recentMove || !recentMove.previousParent) return;
    setUndoBusy(true);
    const body =
      recentMove.previousParent.kind === "broader"
        ? {
            uri: recentMove.conceptUri,
            graph: graph!,
            broader: recentMove.previousParent.uri,
          }
        : {
            uri: recentMove.conceptUri,
            graph: graph!,
            scheme: recentMove.previousParent.uri,
          };
    const result = await patchConcept(body, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, null);
      setRecentMove(null);
    }
    // On failure (rare — would mean the previous parent got deleted
    // between the move and the undo), leave the banner up so the user
    // sees the error. Reset busy either way so the button isn't stuck.
    setUndoBusy(false);
  }

  return (
    <div className="container mx-auto py-6">
      <VdbBuildingNotice className="mb-3" />
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-tres-text-primary">
            Vocabulary
          </h1>
          <p className="text-sm text-tres-text-secondary">
            Browse and curate the vocabulary graph: create schemes and concepts,
            edit, move, and delete them (with edit mode on).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DocExportButtons kind="vocab" graph={graph} />
          <EditModeButton
            on={editMode}
            onToggle={setEditMode}
            visible={canImportData(me.data)}
          />
        </div>
      </div>

      {recentMove && recentMove.previousParent && (
        <div className="mb-3 flex items-center gap-3 rounded-md border border-tres-action/40 bg-tres-submenu-bg px-3 py-2 text-sm">
          <span className="text-tres-text-primary">
            Moved <span className="font-medium">{recentMove.conceptLabel}</span>
            .
          </span>
          <button
            type="button"
            onClick={() => void handleUndoMove()}
            disabled={undoBusy}
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            {undoBusy
              ? "Undoing…"
              : `Undo (restore ${recentMove.previousParent.kind === "scheme" ? "as top of" : "under"} ${recentMove.previousParent.label})`}
          </button>
          <button
            type="button"
            onClick={() => setRecentMove(null)}
            disabled={undoBusy}
            className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'ml-auto')}
            aria-label="Dismiss"
            title="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(280px,1fr)_2fr]">
        {/* Tree panel */}
        <div className="rounded-md border border-border bg-card">
          <div className="border-b border-border px-3 py-2">
            {canEdit && (
              <div className="mb-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => setSchemeModalOpen(true)}
                  disabled={!graph}
                  className={buttonVariants({ variant: 'outline', size: 'sm' })}
                  title={qros ? "Create a new vocabulary class in this graph" : "Create a new concept scheme in this graph"}
                >
                  {qros ? '+ Vocabulary' : '+ Scheme'}
                </button>
              </div>
            )}
            <div className="relative">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search concepts…"
                className="w-full rounded-md border border-border px-3 py-1.5 pr-8 text-sm text-tres-text-primary placeholder:text-tres-text-secondary focus:border-tres-action focus:outline-none focus:ring-1 focus:ring-tres-action"
              />
              {isFiltering && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'absolute right-2 top-1/2')}
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="mt-1.5 flex items-center justify-between text-xs text-tres-text-secondary">
              <span>
                {isFiltering ? (
                  <>
                    {filteredCounts.schemes}/{totalCounts.schemes} scheme
                    {totalCounts.schemes === 1 ? "" : "s"} ·{" "}
                    {filteredCounts.concepts}/{totalCounts.concepts} concept
                    {totalCounts.concepts === 1 ? "" : "s"} match
                  </>
                ) : (
                  <>
                    {totalCounts.schemes} scheme
                    {totalCounts.schemes === 1 ? "" : "s"} ·{" "}
                    {totalCounts.concepts} concept
                    {totalCounts.concepts === 1 ? "" : "s"}
                  </>
                )}
              </span>
              <span className="flex items-center gap-2">
                {treeQuery.isFetching && (
                  <span className="text-tres-status-pending">Loading…</span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const tree = filteredTree ?? treeQuery.data;
                    if (tree) setExpanded(new Set(collectAllUris(tree)));
                  }}
                  className={buttonVariants({ variant: 'link', size: 'md' })}
                  title="Expand every scheme and concept"
                >
                  Expand all
                </button>
                <button
                  type="button"
                  onClick={() => setExpanded(new Set())}
                  className={buttonVariants({ variant: 'link', size: 'md' })}
                  title="Collapse the whole tree"
                >
                  Collapse all
                </button>
              </span>
            </div>
          </div>

          <div className="max-h-[70vh] overflow-y-auto px-2 py-2">
            {treeQuery.isError && (
              <p className="px-3 py-2 text-sm text-tres-error">
                {(treeQuery.error as Error)?.message ??
                  "Failed to load vocabulary tree."}
              </p>
            )}
            {treeQuery.isSuccess && treeQuery.data.schemes.length === 0 && (
              <p className="px-3 py-2 text-sm text-tres-text-secondary">
                No concept schemes in the vocabulary graph yet. Load a vocabulary via the
                Database Management page (kind:{" "}
                <code className="font-mono">vocabulary</code>) to populate.
              </p>
            )}
            {treeQuery.isSuccess &&
              treeQuery.data.schemes.length > 0 &&
              filteredTree === null && (
                <p className="px-3 py-2 text-sm text-tres-text-secondary">
                  No concepts match “{searchQuery}”.
                </p>
              )}
            {deleting && (
              <Message kind="platform" tone="info" density="compact" className="mx-2 mb-1">
                Deleting {deleting.label}{deleting.members > 0 ? ` and ${deleting.members} concept${deleting.members === 1 ? "" : "s"}` : ""}…
              </Message>
            )}
            {deleteProblem && (
              <Message kind="warning" density="compact" className="mx-2 mb-1">{deleteProblem}</Message>
            )}
            {filteredTree && filteredTree.schemes.length > 0 && (
              <ul className="text-sm">
                {filteredTree.schemes.map((scheme) => (
                  <SchemeRow
                    key={scheme.uri}
                    scheme={scheme}
                    expanded={expanded}
                    onToggle={toggleNode}
                    selectedUri={selectedUri}
                    onSelect={(u) => {
                      setSelectedClassUri(null);
                      setSelectedUri(u);
                    }}
                    onSelectScheme={(u) => {
                      setSelectedUri(null);
                      setSelectedClassUri(u);
                    }}
                    searchHighlight={searchQuery.trim().toLowerCase()}
                    canEdit={canEdit}
                    onAddTopConcept={(s) =>
                      setConceptModalParent({
                        kind: "scheme",
                        uri: s.uri,
                        label: s.pref_label,
                      })
                    }
                    onAddNarrower={(c) =>
                      setConceptModalParent({
                        kind: "concept",
                        uri: c.uri,
                        label: c.pref_label,
                      })
                    }
                    onEditScheme={setSchemeToEdit}
                    onDeleteScheme={setSchemeToDelete}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Detail panel */}
        <div className="rounded-md border border-border bg-card">
          <div className="border-b border-border px-4 py-2 text-xs text-tres-text-secondary">
            Concept detail
          </div>
          <div className="px-5 py-4">
            {!selectedUri && (
              <p className="text-sm text-tres-text-secondary">
                Select a concept on the left to see its details.
              </p>
            )}
            {selectedClassUri && !selectedUri && (
              <div>
                <h2 className="text-lg font-semibold text-tres-text-primary">
                  {classEntity.data?.labels.pref_label ?? classEntity.data?.labels.rdfs_label ?? selectedClassUri}
                </h2>
                <p className="mb-3 break-all font-mono text-xs text-tres-text-secondary">
                  {selectedClassUri}
                </p>
                {classEntity.isLoading && (
                  <p className="text-sm text-tres-text-secondary">Loading…</p>
                )}
                {classEntity.data && (
                  <EntityView data={classEntity.data} graph={ALL_GRAPHS} embedded />
                )}
              </div>
            )}
            {selectedUri && detailQuery.isLoading && (
              <p className="text-sm text-tres-text-secondary">Loading…</p>
            )}
            {selectedUri && detailQuery.isError && (
              <p className="text-sm text-tres-error">
                {(detailQuery.error as Error)?.message ??
                  "Failed to load concept detail."}
              </p>
            )}
            {selectedUri && detailQuery.isSuccess && graph && (
              <ConceptDetailPanel
                detail={detailQuery.data}
                graph={graph}
                canEdit={canEdit}
                qros={qros}
                onSelect={setSelectedUri}
                onDeleted={() => setSelectedUri(null)}
                onMoveRequest={() =>
                  setMoveModalFor({
                    uri: detailQuery.data.uri,
                    label: detailQuery.data.pref_label,
                  })
                }
              />
            )}
          </div>
        </div>
      </div>

      {graph && (
        <>
          <EditSchemeModal
            scheme={schemeToEdit}
            graph={graph ?? ""}
            onClose={() => setSchemeToEdit(null)}
            onSaved={() => setSchemeToEdit(null)}
          />
          <DeleteSchemeModal
            scheme={schemeToDelete}
            graph={graph ?? ""}
            onClose={() => setSchemeToDelete(null)}
            onConfirm={(choice) => {
              const target = schemeToDelete;
              setSchemeToDelete(null);
              if (target) void runSchemeDelete(target, choice);
            }}
          />
          <NewSchemeModal
            open={schemeModalOpen}
            graph={graph}
            onClose={() => setSchemeModalOpen(false)}
            onCreated={(created) => {
              setSchemeModalOpen(false);
              // Tree query was invalidated by the modal; pre-select the
              // new scheme's first child slot so the user sees what they
              // just made. Selecting the scheme URI itself doesn't show a
              // detail panel since detail is concept-scoped, but the user
              // will see the new scheme appear in the tree.
              setExpanded((prev) => new Set([...prev, created.uri]));
            }}
          />
        </>
      )}
      {graph && (
        <NewConceptModal
          open={conceptModalParent !== null}
          graph={graph}
          parent={conceptModalParent}
          onClose={() => setConceptModalParent(null)}
          onCreated={(created) => {
            setConceptModalParent(null);
            // Reveal + select the new concept so the user sees the
            // detail panel populate immediately.
            setExpanded((prev) => new Set([...prev, created.uri]));
            setSelectedUri(created.uri);
          }}
        />
      )}
      {graph && moveModalFor && (
        <MoveConceptModal
          open={true}
          graph={graph}
          conceptUri={moveModalFor.uri}
          conceptLabel={moveModalFor.label}
          tree={treeQuery.data}
          onClose={() => setMoveModalFor(null)}
          onMoved={(previousParent) => {
            // Tree + concept-detail queries were invalidated in the
            // modal; the panel + tree will refetch and reveal the
            // new placement. Capture the pre-move parent for Undo.
            const captured = moveModalFor;
            setMoveModalFor(null);
            if (captured && previousParent) {
              setRecentMove({
                conceptUri: captured.uri,
                conceptLabel: captured.label,
                previousParent,
              });
            }
          }}
        />
      )}
    </div>
  );
}

interface SchemeRowProps {
  scheme: SchemeNode;
  expanded: Set<string>;
  onToggle: (uri: string) => void;
  selectedUri: string | null;
  onSelectScheme: (uri: string) => void;
  onSelect: (uri: string) => void;
  /** Lowercased substring to highlight in concept labels. Empty = no highlight. */
  searchHighlight: string;
  canEdit: boolean;
  onAddTopConcept: (scheme: SchemeNode) => void;
  onAddNarrower: (concept: ConceptNode) => void;
  onEditScheme: (scheme: SchemeNode) => void;
  onDeleteScheme: (scheme: SchemeNode) => void;
}

function SchemeRow({
  scheme,
  expanded,
  onToggle,
  selectedUri,
  onSelect,
  onSelectScheme,
  searchHighlight,
  canEdit,
  onAddTopConcept,
  onAddNarrower,
  onEditScheme,
  onDeleteScheme,
}: SchemeRowProps) {
  const isOpen = expanded.has(scheme.uri);
  return (
    <li className="border-b border-border last:border-b-0">
      <div className="group flex items-center gap-1 px-2 py-1.5 text-tres-text-primary hover:bg-muted">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle(scheme.uri);
          }}
          className="flex items-center"
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
        >
          <Caret open={isOpen} />
        </button>
        <button
          type="button"
          onClick={() => onSelectScheme(scheme.uri)}
          // min-w-0 lets a long name shrink inside the flex row; without it the
          // name kept its full width and pushed the row's buttons out of the
          // panel (UAT 2026-09-21, a scheme named by its URI).
          className="flex min-w-0 flex-1 items-center gap-1 text-left hover:underline"
        >
          <span className="truncate font-semibold" title={scheme.pref_label}>{scheme.pref_label}</span>
          <span className="ml-2 shrink-0 whitespace-nowrap text-xs text-tres-text-secondary">
            {scheme.top_concepts.length} top
          </span>
        </button>
        <CopyUriButton
          uri={scheme.uri}
          className="opacity-0 group-hover:opacity-100"
        />
        {canEdit && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAddTopConcept(scheme);
            }}
            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'opacity-0 group-hover:opacity-100')}
            title={`Add a top concept under ${scheme.pref_label}`}
          >
            + Concept
          </button>
        )}
        {canEdit && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEditScheme(scheme);
              }}
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'opacity-0 group-hover:opacity-100')}
              title={`Edit ${scheme.pref_label}`}
            >
              Edit
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteScheme(scheme);
              }}
              className={cn(buttonVariants({ variant: 'danger', size: 'sm' }), 'opacity-0 group-hover:opacity-100')}
              title={`Delete ${scheme.pref_label} and all its concepts`}
            >
              Delete
            </button>
          </>
        )}
      </div>
      {scheme.description && isOpen && (
        <p className="ml-6 mr-2 mb-1 text-xs italic text-tres-text-secondary">
          {scheme.description}
        </p>
      )}
      {isOpen && scheme.top_concepts.length > 0 && (
        <ul>
          {scheme.top_concepts.map((c) => (
            <ConceptRow
              key={c.uri}
              concept={c}
              depth={1}
              expanded={expanded}
              onToggle={onToggle}
              selectedUri={selectedUri}
              onSelect={onSelect}
              searchHighlight={searchHighlight}
              canEdit={canEdit}
              onAddNarrower={onAddNarrower}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

interface ConceptRowProps {
  concept: ConceptNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (uri: string) => void;
  selectedUri: string | null;
  onSelect: (uri: string) => void;
  searchHighlight: string;
  canEdit: boolean;
  onAddNarrower: (concept: ConceptNode) => void;
}

function ConceptRow({
  concept,
  depth,
  expanded,
  onToggle,
  selectedUri,
  onSelect,
  searchHighlight,
  canEdit,
  onAddNarrower,
}: ConceptRowProps) {
  const hasChildren = concept.children.length > 0;
  const isOpen = expanded.has(concept.uri);
  const isSelected = selectedUri === concept.uri;
  // Tailwind purges classes it can't see literally. Pre-listed indents
  // up to 8 levels deep — taxonomies deeper than that are pathological
  // and a Phase 2+ "depth limit" warning can take care of them.
  const indents = [
    "",
    "pl-6",
    "pl-10",
    "pl-14",
    "pl-[4.5rem]",
    "pl-[5.5rem]",
    "pl-24",
    "pl-28",
  ];
  const indentClass = indents[Math.min(depth, indents.length - 1)] ?? "pl-28";

  return (
    <li>
      <div
        className={cn(
          "group flex items-center gap-1 py-1 pr-2 text-tres-text-primary hover:bg-muted",
          indentClass,
          isSelected && "bg-tres-submenu-bg",
        )}
      >
        <button
          type="button"
          onClick={() => (hasChildren ? onToggle(concept.uri) : undefined)}
          className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'flex h-5 w-5 items-center justify-center')}
          aria-label={
            hasChildren ? (isOpen ? "Collapse" : "Expand") : undefined
          }
        >
          {hasChildren ? (
            <Caret open={isOpen} />
          ) : (
            <span className="text-xs">·</span>
          )}
        </button>
        <button
          type="button"
          onClick={() => onSelect(concept.uri)}
          className="flex-1 text-left hover:underline"
        >
          <Highlighted text={concept.pref_label} needle={searchHighlight} />
        </button>
        <CopyUriButton
          uri={concept.uri}
          className="opacity-0 group-hover:opacity-100"
        />
        {canEdit && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAddNarrower(concept);
            }}
            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'opacity-0 group-hover:opacity-100')}
            title={`Add a narrower concept under ${concept.pref_label}`}
          >
            +
          </button>
        )}
      </div>
      {isOpen && hasChildren && (
        <ul>
          {concept.children.map((child) => (
            <ConceptRow
              key={child.uri}
              concept={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              selectedUri={selectedUri}
              onSelect={onSelect}
              searchHighlight={searchHighlight}
              canEdit={canEdit}
              onAddNarrower={onAddNarrower}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

interface ConceptDetailPanelProps {
  detail: ConceptDetail;
  graph: string;
  canEdit: boolean;
  /** QROS-only: gate the read-only statements/tags section (RDF surfaces none). */
  qros: boolean;
  /** Click handler so URI-label rows in broader/narrower lists can navigate. */
  onSelect: (uri: string) => void;
  /** Called after a successful delete so the page can clear selection. */
  onDeleted: () => void;
  /** Open the Move-concept picker for this concept. */
  onMoveRequest: () => void;
}

function ConceptDetailPanel({
  detail,
  graph,
  canEdit,
  qros,
  onSelect,
  onDeleted,
  onMoveRequest,
}: ConceptDetailPanelProps) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  // QROS-only: surfaces the concept's statement tags and constraints that
  // the curated taxonomy fields above don't render. Resolves across graphs.
  const statementEntity = useEntity(qros ? detail.uri : null, ALL_GRAPHS);
  // Single edit mode covers prefLabel + annotations together. Per UAT
  // 2026-05-12: one Edit button, not separate per-field affordances.
  const [editing, setEditing] = useState(false);
  const [editPrefLabel, setEditPrefLabel] = useState("");
  const [editAltLabels, setEditAltLabels] = useState("");
  const [editAnnotations, setEditAnnotations] = useState<AnnotationValue[]>([]);
  // The concept's full desired class set (chips). Cleaning up a bad import means
  // removing specific classes, so this is a list, not a single select.
  const [editClasses, setEditClasses] = useState<string[]>([]);
  const [addClass, setAddClass] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const token = auth.user?.id_token;

  // Known classes for the optional additional-class selector (select-only).
  const classesQuery = useQuery({
    // All graphs (null): class definitions live in the ontology graph, not the
    // vocabulary graph being edited.
    queryKey: ["vdb", "top-classes", "all"],
    queryFn: () => fetchVdbTopClasses(token as string, null),
    enabled: editing && !!token,
  });

  // Reset edit + delete state whenever a different concept gets
  // selected — otherwise stale "are you sure" prompts hang around.
  useEffect(() => {
    setEditing(false);
    setEditPrefLabel("");
    setEditAltLabels("");
    setEditAnnotations([]);
    setEditClasses([]);
    setAddClass("");
    setBusy(false);
    setError(null);
    setConfirmingDelete(false);
  }, [detail.uri]);

  function startEdit() {
    setError(null);
    setEditPrefLabel(detail.pref_label);
    // Comma-separated, the desktop convention; replace-not-append on save.
    setEditAltLabels(detail.alt_labels.map((l) => l.value).join(", "));
    // The full annotation set the editor round-trips (replace-set on save).
    setEditAnnotations(detail.annotations.map((a) => ({ ...a })));
    // All of the concept's current classes (a bad import may have added several).
    // Dedup — a class must appear once (defensive; the backend now scopes to one
    // graph so cross-graph duplicates no longer arrive).
    setEditClasses([...new Set(detail.extra_types.map((t) => t.uri))]);
    setAddClass("");
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setEditPrefLabel("");
    setEditAltLabels("");
    setEditAnnotations([]);
    setEditClasses([]);
    setAddClass("");
    setError(null);
    setConfirmingDelete(false);
  }

  async function saveEdit() {
    if (!token) return;
    const trimmedLabel = editPrefLabel.trim();
    if (trimmedLabel.length === 0) {
      setError("Preferred label cannot be empty.");
      return;
    }
    const body: PatchConceptRequest = { uri: detail.uri, graph };
    // Only send fields that actually changed, so a save that touched
    // only prefLabel doesn't waste a no-op DELETE/INSERT on the
    // annotations (and vice versa).
    if (trimmedLabel !== detail.pref_label) {
      body.pref_label = trimmedLabel;
    }
    const nextAnnotations = editAnnotations
      .map((a) => ({ ...a, value: a.value.trim() }))
      .filter((a) => a.value !== "");
    const annKey = (x: AnnotationValue) =>
      `${x.predicate}\u0001${x.value}\u0001${x.lang ?? ""}`;
    const currentKeys = detail.annotations.map(annKey).sort();
    const nextKeys = nextAnnotations.map(annKey).sort();
    const sameAnnotations =
      nextKeys.length === currentKeys.length &&
      nextKeys.every((v, i) => v === currentKeys[i]);
    if (!sameAnnotations) {
      body.annotations = nextAnnotations;
    }
    // Full-set replace of the concept's classes. Compare as sets (order-agnostic);
    // send the whole desired list when it differs so the backend reconciles.
    const currentClasses = detail.extra_types.map((t) => t.uri);
    const sameClasses =
      editClasses.length === currentClasses.length &&
      editClasses.every((c) => currentClasses.includes(c));
    if (!sameClasses) {
      body.classes = editClasses;
    }
    const newAlts = editAltLabels
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const existingAlts = detail.alt_labels.map((l) => l.value);
    if (
      newAlts.length !== existingAlts.length ||
      newAlts.some((a, i) => a !== existingAlts[i])
    ) {
      // Replaces the whole set; [] clears (desktop semantics).
      body.alt_labels = newAlts;
    }
    if (
      body.pref_label === undefined &&
      body.annotations === undefined &&
      body.classes === undefined &&
      body.alt_labels === undefined
    ) {
      // No changes — just exit edit mode silently.
      cancelEdit();
      return;
    }
    setBusy(true);
    setError(null);
    const result = await patchConcept(body, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, null);
      cancelEdit();
    } else {
      setError(
        result.message,
      );
    }
    setBusy(false);
  }

  async function handleDelete() {
    if (!token) return;
    setBusy(true);
    setError(null);
    const result = await deleteConcept({ uri: detail.uri, graph }, token);
    if (result.ok) {
      invalidateAfterEdit(queryClient, null);
      // Don't reset busy — the panel disappears as the page clears
      // its selection.
      onDeleted();
    } else {
      // 409 with narrower terms carries `detail` from the backend
      // listing the blockers ("delete its 3 narrower term(s) first:
      // …"). Show that verbatim so the taxonomist knows what to fix.
      setError(result.message);
      setBusy(false);
      setConfirmingDelete(false);
    }
  }

  if (editing) {
    return (
      <div className="space-y-3 rounded border border-tres-action/40 bg-tres-submenu-bg/40 p-3">
        <p className="text-xs uppercase tracking-wide text-tres-text-secondary">
          Edit concept
        </p>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-tres-text-secondary">
            Preferred label
          </span>
          <input
            type="text"
            value={editPrefLabel}
            onChange={(e) => setEditPrefLabel(e.target.value)}
            disabled={busy}
            autoFocus
            maxLength={200}
            className="w-full rounded border border-border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-tres-action/40"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-tres-text-secondary">
            Alternative labels
            <span className="ml-2 font-normal text-tres-text-secondary">
              (comma-separated; replaces the whole list)
            </span>
          </span>
          <input
            type="text"
            value={editAltLabels}
            onChange={(e) => setEditAltLabels(e.target.value)}
            disabled={busy}
            maxLength={500}
            className="w-full rounded border border-border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-tres-action/40"
            placeholder="e.g. Blade, Sabre"
          />
        </label>

        <AnnotationListEditor
          value={editAnnotations}
          onChange={setEditAnnotations}
          predicates={CONCEPT_ANNOTATION_PREDICATES}
          disabled={busy}
        />

        <div className="block">
          <span className="mb-1 block text-sm font-medium text-tres-text-primary">
            Classes
            <span className="ml-2 font-normal text-tres-text-secondary">
              (this concept is typed as)
            </span>
          </span>
          {editClasses.length === 0 ? (
            <p className="mb-1 text-xs text-tres-text-secondary">
              No class — this concept won’t appear under any class in the tree.
            </p>
          ) : (
            <div className="mb-1 flex flex-wrap gap-1">
              {editClasses.map((uri) => {
                // Label the chip from the concept's OWN class label (consistent
                // with the read-only detail view), then the ontology-class list,
                // then the raw URI — so two distinct URIs never collapse to the
                // same-looking chip (which read as "a removed class reappeared").
                const label =
                  detail.extra_types.find((t) => t.uri === uri)?.pref_label ||
                  (classesQuery.data ?? []).find((c) => c.uri === uri)?.label ||
                  uri;
                return (
                  <span
                    key={uri}
                    className="inline-flex items-center gap-1 rounded bg-tres-action/10 px-2 py-0.5 text-xs"
                  >
                    <span className="break-all">{label}</span>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        setEditClasses(editClasses.filter((c) => c !== uri))
                      }
                      aria-label={`Remove ${label}`}
                      className={buttonVariants({ variant: 'ghost', size: 'icon' })}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          <select
            value={addClass}
            onChange={(e) => {
              const uri = e.target.value;
              if (uri && !editClasses.includes(uri)) {
                setEditClasses([...editClasses, uri]);
              }
              setAddClass("");
            }}
            disabled={busy}
            className="w-full rounded border border-border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-tres-action/40"
          >
            <option value="">+ add a class…</option>
            {(classesQuery.data ?? [])
              .filter((cls) => !editClasses.includes(cls.uri))
              .map((cls) => (
                <option key={cls.uri} value={cls.uri}>
                  {cls.label || cls.uri}
                </option>
              ))}
          </select>
        </div>

        <p className="flex items-start gap-1 break-all font-mono text-[10px] text-tres-text-secondary">
          <span className="min-w-0">{detail.uri}</span>
          <CopyUriButton uri={detail.uri} />
        </p>

        {error && (
          <div className={messageClass('warning', 'normal')}>
            {error}
          </div>
        )}

        {confirmingDelete ? (
          <div className={cn(messageClass('warning', 'normal'), 'flex flex-wrap items-center gap-2')}>
            <span className="text-tres-text-primary">
              Delete <span className="font-medium">{detail.pref_label}</span>?
            </span>
            <span className="text-xs text-tres-text-secondary">
              Cannot be undone. Narrower terms must be deleted first.
            </span>
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                disabled={busy}
                className={buttonVariants({ variant: 'secondary', size: 'sm' })}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={busy}
                className={buttonVariants({ variant: 'dangerFilled', size: 'sm' })}
              >
                {busy ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                setError(null);
                setConfirmingDelete(true);
              }}
              disabled={busy}
              className={buttonVariants({ variant: 'danger', size: 'sm' })}
            >
              Delete…
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={cancelEdit}
                disabled={busy}
                className={buttonVariants({ variant: 'secondary', size: 'md' })}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void saveEdit()}
                disabled={busy}
                className={buttonVariants({ variant: 'primary', size: 'md' })}
              >
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Read mode.
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-tres-text-primary">
            {detail.pref_label}
          </h2>
          <p className="mt-0.5 flex items-start gap-1 break-all font-mono text-xs text-tres-text-secondary">
            <span className="min-w-0">{detail.uri}</span>
            <CopyUriButton uri={detail.uri} />
          </p>
        </div>
        {canEdit && (
          <div className="flex shrink-0 gap-1">
            <button
              type="button"
              onClick={startEdit}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
              title="Edit preferred label, definition, or delete"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onMoveRequest}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
              title="Move under a different parent or scheme"
            >
              Move
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className={messageClass('warning', 'normal')}>
          {error}
        </div>
      )}

      {detail.alt_labels.length > 0 && (
        <DetailSection title="Also known as">
          <ul className="flex flex-wrap gap-1.5">
            {detail.alt_labels.map((lv, i) => (
              <li
                key={`${lv.value}-${lv.lang ?? ""}-${i}`}
                className="inline-flex items-baseline gap-1 rounded border border-border bg-muted px-2 py-0.5 text-sm text-tres-text-primary"
              >
                <span>{lv.value}</span>
                {lv.lang && (
                  <span className="font-mono text-[10px] uppercase text-tres-text-secondary">
                    {lv.lang}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </DetailSection>
      )}

      <DetailSection title="Definition">
        {annotationValues(detail, ":definition").length > 0 ? (
          <LangValueList values={annotationValues(detail, ":definition")} />
        ) : (
          <p className="text-sm italic text-tres-text-secondary">
            No definition.
          </p>
        )}
      </DetailSection>

      {annotationValues(detail, ":scopeNote").length > 0 && (
        <DetailSection title="Scope note">
          <LangValueList values={annotationValues(detail, ":scopeNote")} />
        </DetailSection>
      )}

      {annotationValues(detail, ":comment", ":note", ":example").length > 0 && (
        <DetailSection title="Notes">
          <LangValueList
            values={annotationValues(detail, ":comment", ":note", ":example")}
          />
        </DetailSection>
      )}

      {detail.broader.length > 0 && (
        <DetailSection title="Broader">
          <UriLabelList items={detail.broader} onSelect={onSelect} />
        </DetailSection>
      )}

      {detail.narrower.length > 0 && (
        <DetailSection title="Narrower">
          <UriLabelList items={detail.narrower} onSelect={onSelect} />
        </DetailSection>
      )}
      {detail.broader.length === 0 && detail.narrower.length === 0 && (
        <p className="text-sm text-tres-text-secondary">
          Standalone concept (no broader or narrower relations).
        </p>
      )}

      {detail.extra_types.length > 0 && (
        <DetailSection
          title={detail.extra_types.length > 1 ? "Classes" : "Class"}
        >
          <ul className="flex flex-wrap gap-1.5">
            {detail.extra_types.map((t) => (
              <li
                key={t.uri}
                className="inline-flex items-center gap-1 rounded border border-tres-action/30 bg-tres-submenu-bg px-2 py-0.5 text-sm text-tres-text-primary"
                title={t.uri}
              >
                {t.pref_label}
                <CopyUriButton uri={t.uri} />
              </li>
            ))}
          </ul>
        </DetailSection>
      )}

      {qros && statementEntity.data && (
        <div className="mt-6 border-t border-border pt-4">
          <EntityView data={statementEntity.data} graph={ALL_GRAPHS} embedded />
        </div>
      )}
    </div>
  );
}

function DetailSection({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-tres-text-secondary">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function LangValueList({ values }: { values: LangValue[] }) {
  return (
    <ul className="space-y-1 text-sm text-tres-text-primary">
      {values.map((lv, i) => (
        <li
          key={`${lv.value}-${lv.lang ?? ""}-${i}`}
          className="flex items-baseline gap-2"
        >
          <span className="flex-1">{lv.value}</span>
          {lv.lang && (
            <span className="font-mono text-[10px] uppercase text-tres-text-secondary">
              {lv.lang}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

function UriLabelList({
  items,
  onSelect,
}: {
  items: UriLabel[];
  onSelect: (uri: string) => void;
}) {
  return (
    <ul className="space-y-0.5 text-sm">
      {items.map((it) => (
        <li key={it.uri} className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onSelect(it.uri)}
            className={cn(buttonVariants({ variant: 'link', size: 'md' }), 'text-left')}
            title={it.uri}
          >
            {it.pref_label}
          </button>
          <CopyUriButton uri={it.uri} />
        </li>
      ))}
    </ul>
  );
}

/** Wrap occurrences of `needle` (case-insensitive) in <mark> tags. */
function Highlighted({ text, needle }: { text: string; needle: string }) {
  if (!needle) return <>{text}</>;
  const lower = text.toLowerCase();
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  while (cursor < text.length) {
    const idx = lower.indexOf(needle, cursor);
    if (idx === -1) {
      parts.push(text.slice(cursor));
      break;
    }
    if (idx > cursor) parts.push(text.slice(cursor, idx));
    parts.push(
      <mark
        key={`${idx}-${needle}`}
        className="bg-tres-warning/30 px-0 text-tres-text-primary"
      >
        {text.slice(idx, idx + needle.length)}
      </mark>,
    );
    cursor = idx + needle.length;
  }
  return <>{parts}</>;
}

function Caret({ open }: { open: boolean }) {
  return (
    <span
      className={cn(
        "inline-block h-3 w-3 transform text-tres-text-secondary transition-transform",
        open ? "rotate-90" : "rotate-0",
      )}
      aria-hidden
    >
      ▶
    </span>
  );
}

function countNodes(tree: VocabTreeResponse | undefined): {
  schemes: number;
  concepts: number;
} {
  if (!tree) return { schemes: 0, concepts: 0 };
  let concepts = 0;
  for (const s of tree.schemes) {
    for (const c of s.top_concepts) {
      concepts += countConceptNodes(c);
    }
  }
  return { schemes: tree.schemes.length, concepts };
}

function countConceptNodes(c: ConceptNode): number {
  return 1 + c.children.reduce((acc, x) => acc + countConceptNodes(x), 0);
}
